/**
 * What became of the changes a parent asked for.
 *
 * A request that is still waiting says so and lists what is in it, because the
 * most common question after sending one is "did that go anywhere?". Once the
 * school has reviewed it, the banner says what was applied and what wasn't —
 * a declined change is not an error, and the parent should hear it either way.
 */
import { Clock, CheckCircle2 } from 'lucide-react';

export interface ChangeRequestItem {
  key: string;
  label: string;
  decision?: string | null;
}

export interface ChangeRequest {
  state: string;
  /** ISO8601DateTime reaches the client as `unknown`; formatDate narrows it. */
  submittedAt: unknown;
  reviewedAt?: unknown;
  adminNote?: string | null;
  acceptedCount: number;
  items: ChangeRequestItem[];
}

const formatDate = (value: unknown) =>
  typeof value === 'string' ? new Date(value).toLocaleDateString(undefined, { month: 'long', day: 'numeric' }) : '';

export function PendingChangesBanner({
  pending,
  lastReviewed,
}: {
  pending?: ChangeRequest | null;
  lastReviewed?: ChangeRequest | null;
}) {
  if (pending) {
    const count = pending.items.length;
    return (
      <div className="flex gap-3 rounded-lg border border-orange-200 bg-orange-50 p-4 text-sm dark:border-orange-900 dark:bg-orange-950/40">
        <Clock className="mt-0.5 h-4 w-4 shrink-0 text-orange-600" />
        <div>
          <p className="font-medium text-orange-900 dark:text-orange-200">
            {count} {count === 1 ? 'change is' : 'changes are'} waiting for the school to review
          </p>
          <p className="mt-1 text-orange-800 dark:text-orange-300">
            Sent {formatDate(pending.submittedAt)} · {pending.items.map((item) => item.label).join(', ')}
          </p>
          <p className="mt-1 text-orange-800/80 dark:text-orange-300/80">
            The details below still show what the school has on file today.
          </p>
        </div>
      </div>
    );
  }

  if (!lastReviewed) return null;

  const total = lastReviewed.items.length;
  const declined = lastReviewed.items.filter((item) => item.decision === 'DECLINED');

  return (
    <div className="flex gap-3 rounded-lg border bg-muted/50 p-4 text-sm">
      <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
      <div>
        <p className="font-medium">
          The school reviewed your request on {formatDate(lastReviewed.reviewedAt)} — {lastReviewed.acceptedCount} of{' '}
          {total} {total === 1 ? 'change' : 'changes'} applied
        </p>
        {declined.length > 0 && (
          <p className="mt-1 text-muted-foreground">
            Not applied: {declined.map((item) => item.label).join(', ')}
          </p>
        )}
        {lastReviewed.adminNote && <p className="mt-1 text-muted-foreground">“{lastReviewed.adminNote}”</p>}
      </div>
    </div>
  );
}

export default PendingChangesBanner;
